import { useState, useEffect, useMemo } from 'react'
import { BrCard } from '@govbr-ds/react-components'
import { getExecutions, ExecutionWithId } from '../services/firestore-proxy'

interface UsageMetrics {
  total_evaluations: number
  total_attempts: number
  incomplete_sessions: number
  total_sessions: number
  avg_clicks: number
  avg_duration_ms: number
  avg_duration_min: number
  min_duration_ms: number
  max_duration_ms: number
  success_rate: number
}

// Helper to normalize percentage values - handles values stored as 0-100 or 0-1
function normalizePercentage(value: number | null): number | null {
  if (value === null) return null
  // If value is > 1, it's already a percentage (0-100 scale)
  // If value is <= 1, it's a decimal (0-1 scale) - multiply by 100
  return value > 1 ? value : value * 100
}

// Check if an execution has valid metrics for averaging
function hasValidMetrics(exec: ExecutionWithId): boolean {
  const m = extractMetrics(exec)
  // Consider valid if at least TADS and execution_time are present
  return m.tads !== null && m.execution_time_ms !== null
}

// Helper to extract metrics from execution data
// Handles multiple possible data structures from different pipeline versions
interface ExtractedMetrics {
  tads: number | null
  flesch_index: number | null
  legal_term_ratio: number | null
  information_density: number | null
  component_count: number | null
  tree_depth: number | null
  execution_time_ms: number | null
  retry_count: number
}

function extractMetrics(exec: ExecutionWithId): ExtractedMetrics {
  // Try to get metrics from different possible locations
  const metrics = exec.metrics
  // Cast to allow access to validation_result which may exist in older data structures
  const execRecord = exec as unknown as Record<string, unknown>
  const validation = execRecord.validation_result as Record<string, unknown> | undefined

  // Helper to get a numeric value from multiple possible sources
  const getNum = (
    metricsKey: string,
    validationKey?: string,
    directKey?: string
  ): number | null => {
    // First try metrics object
    if (metrics && typeof metrics[metricsKey as keyof typeof metrics] === 'number') {
      const val = metrics[metricsKey as keyof typeof metrics] as number
      if (!isNaN(val)) return val
    }
    // Then try validation_result
    if (validation && validationKey) {
      const val = validation[validationKey]
      if (typeof val === 'number' && !isNaN(val)) return val
    }
    // Then try direct field on execution
    if (directKey) {
      const val = execRecord[directKey]
      if (typeof val === 'number' && !isNaN(val)) return val
    }
    return null
  }

  // Calculate TADS from validation_result if not directly available
  let tads = getNum('tads', 'tads', 'tads')
  if (tads === null && validation) {
    const coverage = validation.component_coverage
    const propValidity = validation.prop_validity
    if (typeof coverage === 'number' && typeof propValidity === 'number') {
      tads = (coverage * propValidity) / 100
    }
  }

  return {
    tads,
    flesch_index: getNum('flesch_index', 'flesch_index', 'flesch_index'),
    legal_term_ratio: getNum('legal_term_ratio', 'legal_term_ratio', 'legal_term_ratio'),
    information_density: getNum('information_density', 'information_density', 'information_density'),
    component_count: getNum('component_count', 'component_count', 'component_count'),
    tree_depth: getNum('tree_depth', 'tree_depth', 'tree_depth'),
    execution_time_ms: getNum('execution_time_ms', undefined, 'execution_time_ms'),
    retry_count: getNum('retry_count', undefined, 'retry_count') ?? 0,
  }
}

export function MetricsPage() {
  const [executions, setExecutions] = useState<ExecutionWithId[]>([])
  const [usageMetrics] = useState<UsageMetrics | null>(null)
  const [loading, setLoading] = useState(true)

  // Filters - similar to IQAIPage
  const [completedOnly, setCompletedOnly] = useState(false)
  const [validOnly, setValidOnly] = useState(false)

  useEffect(() => {
    loadExecutions()
    // Note: loadUsageMetrics removed - /api/metrics/usage endpoint not available
    // Usage metrics will be shown when backend endpoint is implemented
  }, [])

  const loadExecutions = async () => {
    try {
      const result = await getExecutions({
        orderBy: 'created_at_desc',
      })

      if (result.success && result.data) {
        setExecutions(result.data)
      }
    } catch (error) {
      console.error('Error loading executions:', error)
    } finally {
      setLoading(false)
    }
  }

  // loadUsageMetrics removed - /api/metrics/usage endpoint not available
  // Usage metrics will be shown when backend endpoint is implemented

  // Apply filters to executions
  const filteredExecutions = useMemo(() => {
    let filtered = executions

    // Filter for completed/successful executions only
    if (completedOnly) {
      filtered = filtered.filter(e => e.status === 'success')
    }

    // Filter for executions with valid metrics only
    if (validOnly) {
      filtered = filtered.filter(e => hasValidMetrics(e))
    }

    return filtered
  }, [executions, completedOnly, validOnly])

  // Calculate counts for filter indicators
  const completedCount = useMemo(
    () => executions.filter(e => e.status === 'success').length,
    [executions]
  )
  const validCount = useMemo(
    () => executions.filter(e => hasValidMetrics(e)).length,
    [executions]
  )

  const calculateAverages = () => {
    // Extract metrics from filtered executions using the helper
    const metricsData = filteredExecutions.map(e => extractMetrics(e))

    // Filter executions that have valid TADS defined
    const withMetrics = metricsData.filter(m => m.tads !== null)
    if (withMetrics.length === 0) return null

    const sums = withMetrics.reduce(
      (acc, m) => {
        // Normalize TADS and legal_term_ratio - they may be stored as 0-100 or 0-1
        const normalizedTads = normalizePercentage(m.tads) ?? 0
        const normalizedLegalRatio = normalizePercentage(m.legal_term_ratio) ?? 0

        acc.tads += normalizedTads
        acc.flesch += m.flesch_index ?? 0
        acc.legalRatio += normalizedLegalRatio
        acc.density += m.information_density ?? 0
        acc.components += m.component_count ?? 0
        acc.depth += m.tree_depth ?? 0
        acc.time += m.execution_time_ms ?? 0
        acc.retries += m.retry_count ?? 0
        return acc
      },
      {
        tads: 0,
        flesch: 0,
        legalRatio: 0,
        density: 0,
        components: 0,
        depth: 0,
        time: 0,
        retries: 0,
      }
    )

    const count = withMetrics.length
    return {
      tads: sums.tads / count,
      flesch: sums.flesch / count,
      legalRatio: sums.legalRatio / count,
      density: sums.density / count,
      components: sums.components / count,
      depth: sums.depth / count,
      time: sums.time / count,
      retries: sums.retries / count,
      count,
    }
  }

  const averages = calculateAverages()

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center min-h-[400px]">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-6">
      <h1 className="text-2xl font-bold text-gray-80">Métricas de Validação</h1>

      {/* TADS Explanation Card */}
      <BrCard className="bg-blue-warm-vivid-5">
        <div className="card-header">
          <span className="title">
            <i className="fas fa-info-circle me-2"></i>
            Sobre o Cálculo do TADS
          </span>
        </div>
        <div className="card-content">
          <div className="d-flex flex-column gap-3">
            <div>
              <h3 className="font-semibold text-gray-80 mb-2">TADS Complexo (exibido abaixo)</h3>
              <p className="text-sm text-gray-70">
                <code>TADS = (Component_Coverage × Property_Validity) / 100</code>
              </p>
              <p className="text-sm text-gray-60 mt-1">
                Avalia tanto a validade dos <strong>tipos de componentes</strong> quanto a validade das <strong>propriedades</strong> de cada componente.
                Penaliza props desconhecidas mesmo que os componentes sejam válidos.
              </p>
            </div>
            <div className="border-t pt-3">
              <h3 className="font-semibold text-gray-80 mb-2">TADS Simplificado (recomendado)</h3>
              <p className="text-sm text-gray-70">
                <code>TADS = (Componentes_Válidos / Total_Componentes) × 100</code>
              </p>
              <p className="text-sm text-gray-60 mt-1">
                Foca <strong>apenas na validade dos tipos de componentes</strong>, verificando se pertencem ao Gov.br Design System oficial (36 componentes + 8 utilitários).
                Mais claro e direto para avaliar aderência ao Design System.
              </p>
            </div>
            <div className="bg-blue-warm-vivid-10 p-3 rounded text-sm">
              <strong className="text-gray-80">Nota:</strong>
              <span className="text-gray-70 ms-2">
                As métricas abaixo utilizam o <strong>TADS Complexo</strong> por compatibilidade com dados históricos.
                Para TADS Simplificado, consulte o relatório em{' '}
                <code className="bg-pure-0 px-1 py-0.5 rounded">docs/resultados/analise_interfaces_tads_simplificado.md</code>
              </span>
            </div>
          </div>
        </div>
      </BrCard>

      {/* Filter Options */}
      <div className="d-flex flex-column gap-2 bg-gray-2 p-3 rounded">
        <div className="d-flex align-items-center gap-2">
          <input
            type="checkbox"
            id="completedOnly"
            checked={completedOnly}
            onChange={(e) => setCompletedOnly(e.target.checked)}
            className="form-check-input"
          />
          <label htmlFor="completedOnly" className="text-sm text-gray-70">
            Calcular métricas apenas com execuções <strong>concluídas com sucesso</strong>
          </label>
          {completedOnly && (
            <span className="text-xs text-primary-default ms-2">
              ({completedCount} de {executions.length} execuções)
            </span>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <input
            type="checkbox"
            id="validOnly"
            checked={validOnly}
            onChange={(e) => setValidOnly(e.target.checked)}
            className="form-check-input"
          />
          <label htmlFor="validOnly" className="text-sm text-gray-70">
            Calcular métricas apenas com execuções <strong>válidas</strong>
            <span className="text-xs text-gray-50 ms-1">(com TADS e tempo de execução registrados)</span>
          </label>
          {validOnly && (
            <span className="text-xs text-primary-default ms-2">
              ({validCount} de {executions.length} execuções)
            </span>
          )}
        </div>
      </div>

      {/* Usage Metrics Section */}
      {usageMetrics && (
        <>
          <h2 className="text-xl font-semibold text-gray-70 mt-4">Métricas de Uso</h2>
          <div className="d-flex flex-column md:flex-row gap-4">
            <BrCard className="text-center flex-1">
              <p className="text-3xl font-bold text-primary-default">
                {usageMetrics.total_evaluations}
              </p>
              <p className="text-sm text-gray-60">Avaliações Concluídas</p>
              <p className="text-xs text-gray-60 mt-1">
                de {usageMetrics.total_attempts} tentativas
              </p>
            </BrCard>
            <BrCard className="text-center flex-1">
              <p className="text-3xl font-bold text-warning-default">
                {usageMetrics.incomplete_sessions}
              </p>
              <p className="text-sm text-gray-60">Sessões Incompletas</p>
              <p className="text-xs text-gray-60 mt-1">
                de {usageMetrics.total_sessions} sessões
              </p>
            </BrCard>
            <BrCard className="text-center flex-1">
              <p className="text-3xl font-bold text-primary-default">
                {usageMetrics.avg_clicks}
              </p>
              <p className="text-sm text-gray-60">Cliques Médios</p>
              <p className="text-xs text-gray-60 mt-1">
                por avaliação
              </p>
            </BrCard>
            <BrCard className="text-center flex-1">
              <p className="text-3xl font-bold text-success-default">
                {usageMetrics.success_rate}%
              </p>
              <p className="text-sm text-gray-60">Taxa de Sucesso</p>
              <p className="text-xs text-gray-60 mt-1">
                avaliações concluídas
              </p>
            </BrCard>
          </div>

          <BrCard>
            <div className="card-header"><span className="title">Tempo de Avaliação</span></div>
            <div className="card-content">
              <div className="d-flex flex-column md:flex-row gap-6">
                <div className="flex-1">
                  <p className="text-2xl font-bold text-gray-80">
                    {usageMetrics.avg_duration_min} min
                  </p>
                  <p className="text-sm text-gray-60">Tempo Médio</p>
                </div>
                <div className="flex-1">
                  <p className="text-2xl font-bold text-gray-80">
                    {Math.round(usageMetrics.min_duration_ms / 60000)} min
                  </p>
                  <p className="text-sm text-gray-60">Mais Rápido</p>
                </div>
                <div className="flex-1">
                  <p className="text-2xl font-bold text-gray-80">
                    {Math.round(usageMetrics.max_duration_ms / 60000)} min
                  </p>
                  <p className="text-sm text-gray-60">Mais Lento</p>
                </div>
              </div>
            </div>
          </BrCard>
        </>
      )}

      {/* Average Metrics */}
      <h2 className="text-xl font-semibold text-gray-70 mt-4">Métricas de Geração</h2>
      {averages && (
        <div className="d-flex flex-column md:flex-row gap-4">
          <BrCard className="text-center flex-1">
            <p className="text-3xl font-bold text-primary-default">
              {averages.tads.toFixed(1)}%
            </p>
            <p className="text-sm text-gray-60">TADS Médio (Complexo)</p>
            <p className="text-xs text-gray-60 mt-1">
              Aderência ao DS (tipos × props)
            </p>
          </BrCard>
          <BrCard className="text-center flex-1">
            <p className="text-3xl font-bold text-primary-default">
              {averages.flesch.toFixed(1)}
            </p>
            <p className="text-sm text-gray-60">Índice Flesch</p>
            <p className="text-xs text-gray-60 mt-1">
              Legibilidade do texto
            </p>
          </BrCard>
          <BrCard className="text-center flex-1">
            <p className="text-3xl font-bold text-primary-default">
              {averages.legalRatio.toFixed(1)}%
            </p>
            <p className="text-sm text-gray-60">Termos Jurídicos</p>
            <p className="text-xs text-gray-60 mt-1">
              Proporção de termos técnicos
            </p>
          </BrCard>
          <BrCard className="text-center flex-1">
            <p className="text-3xl font-bold text-primary-default">
              {averages.time.toFixed(0)}ms
            </p>
            <p className="text-sm text-gray-60">Tempo Médio</p>
            <p className="text-xs text-gray-60 mt-1">
              Tempo de execução
            </p>
          </BrCard>
        </div>
      )}

      {/* Component Metrics */}
      {averages && (
        <BrCard>
          <div className="card-header"><span className="title">Métricas de Componentes</span></div>
          <div className="card-content">
            <div className="d-flex flex-column md:flex-row gap-6">
              <div className="flex-1">
                <p className="text-2xl font-bold text-gray-80">
                  {averages.components.toFixed(1)}
                </p>
                <p className="text-sm text-gray-60">Componentes por UI</p>
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-gray-80">
                  {averages.depth.toFixed(1)}
                </p>
                <p className="text-sm text-gray-60">Profundidade da Árvore</p>
              </div>
              <div className="flex-1">
                <p className="text-2xl font-bold text-gray-80">
                  {averages.density.toFixed(2)}
                </p>
                <p className="text-sm text-gray-60">Densidade de Informação</p>
              </div>
            </div>
          </div>
        </BrCard>
      )}

      {/* Execution History */}
      <BrCard>
        <div className="card-header">
          <span className="title">
            Histórico de Execuções ({filteredExecutions.length}
            {(completedOnly || validOnly) && ` de ${executions.length}`})
          </span>
        </div>
        <div className="card-content">
          <div className="overflow-x-auto">
            <table className="w-100 text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-4">Caso</th>
                  <th className="py-2 pr-4">TADS</th>
                  <th className="py-2 pr-4">Flesch</th>
                  <th className="py-2 pr-4">Termos</th>
                  <th className="py-2 pr-4">Componentes</th>
                  <th className="py-2 pr-4">Tempo</th>
                  <th className="py-2">Retries</th>
                </tr>
              </thead>
              <tbody>
                {filteredExecutions.slice(0, 20).map((exec) => {
                  const m = extractMetrics(exec)
                  const normalizedTads = normalizePercentage(m.tads)
                  const normalizedLegalRatio = normalizePercentage(m.legal_term_ratio)
                  return (
                    <tr key={exec.id} className="border-b">
                      <td className="py-2 pr-4 font-medium">{exec.case_id}</td>
                      <td className="py-2 pr-4">
                        {normalizedTads !== null
                          ? `${normalizedTads.toFixed(0)}%`
                          : '-'}
                      </td>
                      <td className="py-2 pr-4">
                        {m.flesch_index !== null
                          ? m.flesch_index.toFixed(0)
                          : '-'}
                      </td>
                      <td className="py-2 pr-4">
                        {normalizedLegalRatio !== null
                          ? `${normalizedLegalRatio.toFixed(0)}%`
                          : '-'}
                      </td>
                      <td className="py-2 pr-4">
                        {m.component_count !== null ? m.component_count : '-'}
                      </td>
                      <td className="py-2 pr-4">
                        {m.execution_time_ms !== null
                          ? `${m.execution_time_ms}ms`
                          : '-'}
                      </td>
                      <td className="py-2">{m.retry_count}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {filteredExecutions.length > 20 && (
            <p className="text-sm text-gray-60 mt-4 text-center">
              Mostrando 20 de {filteredExecutions.length} execuções
            </p>
          )}
        </div>
      </BrCard>

      {filteredExecutions.length === 0 && (
        <BrCard>
          <p className="text-center text-gray-60">
            {executions.length === 0
              ? 'Nenhuma execução encontrada. Execute os casos de teste para ver as métricas.'
              : 'Nenhuma execução corresponde aos filtros selecionados.'}
          </p>
        </BrCard>
      )}
    </div>
  )
}
